import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const KioskHome = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [shopId, setShopId] = useState("");
  const [shopName, setShopName] = useState("");
  const [categoryId, setCategoryId] = useState(0);
  const [category, setCategory] = useState({ name: "" });
  const [categories, setCategories] = useState({});
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const [cartCount, setCartCount] = useState(0);
  const [showCheckoutBar, setShowCheckoutBar] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("장바구니에 담겼습니다.");

  const [showPaidOverlay, setShowPaidOverlay] = useState(false);

  const [optionModal, setOptionModal] = useState({
    open: false,
    product: null,
    selected: {},
  });

  const pollingRef = useRef({
    cart: null,
    status: null,
    paidHandled: false,
    toastTimer: null,
    paidTimer: null,
  });

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const mode = query.get("mode") || "";
  const queryShopId = query.get("shop_id") || "";
  const queryCategoryId = Number(query.get("id") || 0);
  const added = Number(query.get("added") || 0);
  const addedName = query.get("added_name") || "";

  const normalizeCategories = useCallback((raw) => {
    if (!raw || typeof raw !== "object") return {};

    const normalized = {};
    Object.keys(raw).forEach((key) => {
      const newKey = key === "null" ? "root" : String(key);
      normalized[newKey] = Array.isArray(raw[key]) ? raw[key] : [];
    });
    return normalized;
  }, []);

  const getChildren = useCallback(
    (parentId = null) => {
      const key = parentId == null ? "root" : String(parentId);
      return categories[key] || [];
    },
    [categories]
  );

  const currentRequestUri = useMemo(() => {
    return `/kiosk${location.search || ""}`;
  }, [location.search]);

  const buildApiRedirectPath = useCallback((redirectUrl) => {
    if (!redirectUrl) return null;

    try {
      const parsed = new URL(redirectUrl, window.location.origin);
      const path = parsed.pathname.replace(/^\/api/, "");
      return `${path}${parsed.search}${parsed.hash}`;
    } catch (e) {
      if (redirectUrl.startsWith("/api/")) {
        return redirectUrl.replace(/^\/api/, "");
      }
      return redirectUrl;
    }
  }, []);

  const refreshCartCount = useCallback(async () => {
    try {
      const res = await axios.get("/api/kiosk/cart/count", {
        withCredentials: true,
        params: queryShopId ? { shop_id: queryShopId } : {},
      });

      const count = Number(res.data?.count || 0);
      setCartCount(count);
      setShowCheckoutBar(count > 0);
    } catch (e) {}
  }, [queryShopId]);

  const pollKioskStatus = useCallback(async () => {
    try {
      const res = await axios.get("/api/kiosk/status", {
        withCredentials: true,
        params: queryShopId ? { shop_id: queryShopId } : {},
      });

      const status = res.data?.status || "";

      if (status === "PAID" && !pollingRef.current.paidHandled) {
        pollingRef.current.paidHandled = true;
        setShowPaidOverlay(true);

        if (pollingRef.current.paidTimer) {
          clearTimeout(pollingRef.current.paidTimer);
        }

        pollingRef.current.paidTimer = setTimeout(() => {
          handleReset(true);
        }, 2500);
      }

      if (status !== "PAID") {
        pollingRef.current.paidHandled = false;
        setShowPaidOverlay(false);
      }
    } catch (e) {}
  }, []);

  const fetchHomeData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await axios.get("/api/kiosk", {
        params: {
          ...(queryCategoryId > 0 ? { id: queryCategoryId } : {}),
          ...(queryShopId ? { shop_id: queryShopId } : {}),
          ...(mode ? { mode } : {}),
        },
        withCredentials: true,
      });

      const data = res.data || {};

      if (data.redirect) {
        const target = buildApiRedirectPath(data.redirect);
        if (target) {
          navigate(target, { replace: true });
          return;
        }
      }

      setShopId(String(data.shop_id || queryShopId || ""));
      setShopName(data.shop_name || "");
      setCategoryId(Number(data.category_id || queryCategoryId || 0));
      setCategory(data.category || { name: "" });
      setCategories(normalizeCategories(data.categories));
      setProducts(Array.isArray(data.products) ? data.products : []);
    } catch (err) {
      console.error("키오스크 홈 데이터 조회 실패:", err);
      setShopName("");
      setCategory({ name: "" });
      setCategories({});
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [
    buildApiRedirectPath,
    mode,
    navigate,
    normalizeCategories,
    queryCategoryId,
    queryShopId,
  ]);

  const handleReset = useCallback(
    async (silent = false) => {
      try {
        const res = await axios.post(
          "/api/kiosk/reset",
          {},
          {
            withCredentials: true,
          }
        );

        const redirect = res.data?.redirect;
        if (redirect) {
          const target = buildApiRedirectPath(redirect);
          navigate(target || "/kiosk", { replace: true });
          return;
        }

        navigate(
          queryShopId ? `/kiosk?shop_id=${encodeURIComponent(queryShopId)}` : "/kiosk",
          { replace: true }
        );
      } catch (err) {
        if (!silent) {
          alert(err.response?.data?.message || "초기화 중 오류가 발생했습니다.");
        }
      }
    },
    [buildApiRedirectPath, navigate, queryShopId]
  );

  const submitAddToCart = useCallback(
    async (product, selectedOptionItems = []) => {
      try {
        const selectedOptions = {};

        selectedOptionItems.forEach((opt) => {
          const groupId = Number(opt.group_id || 0);
          const itemId = Number(opt.item_id || 0);

          if (groupId > 0 && itemId > 0) {
            selectedOptions[String(groupId)] = itemId;
          }
        });

        const res = await axios.post(
          "/api/kiosk/cart/add",
          {
            product_id: product.id,
            quantity: 1,
            action: "cart",
            return_url: currentRequestUri,
            added_name: product.name,

            // 기존 사용자 주문 API 형식
            selected_options: selectedOptions,

            // 테이블오더와 동일한 상세 옵션 형식
            selected_option_items: selectedOptionItems,
          },
          { withCredentials: true }
        );

        if (res.data?.redirect || res.data?.success !== false) {
          const name = product.name
            ? `장바구니에 담겼습니다: ${product.name}`
            : "장바구니에 담겼습니다.";

          setToastMessage(name);
          setShowToast(true);
          setOptionModal({
            open: false,
            product: null,
            selected: {},
          });

          if (pollingRef.current.toastTimer) {
            clearTimeout(pollingRef.current.toastTimer);
          }

          pollingRef.current.toastTimer = setTimeout(() => {
            setShowToast(false);
          }, 400);

          await refreshCartCount();
        }
      } catch (err) {
        alert(err.response?.data?.message || "장바구니 담기 중 오류가 발생했습니다.");
      }
    },
    [currentRequestUri, refreshCartCount]
  );

  const handleAddToCart = useCallback(
    (product) => {
      const groups = Array.isArray(product?.option_groups)
        ? product.option_groups
        : [];

      if (groups.length === 0) {
        submitAddToCart(product, []);
        return;
      }

      setOptionModal({
        open: true,
        product,
        selected: {},
      });
    },
    [submitAddToCart]
  );

  const handleOptionChange = useCallback((groupId, itemId) => {
    setOptionModal((prev) => ({
      ...prev,
      selected: {
        ...prev.selected,
        [String(groupId)]: Number(itemId || 0),
      },
    }));
  }, []);

  const confirmOptionProduct = useCallback(() => {
    const product = optionModal.product;
    if (!product) return;

    const groups = Array.isArray(product.option_groups)
      ? product.option_groups
      : [];

    const selectedOptionItems = [];

    for (const group of groups) {
      const selectedItemId = Number(
        optionModal.selected[String(group.id)] || 0
      );

      const required =
        String(group.required || "N").toUpperCase() === "Y";

      if (required && selectedItemId <= 0) {
        alert(`${group.group_name || "필수 옵션"}을 선택해주세요.`);
        return;
      }

      if (selectedItemId <= 0) continue;

      const item = (group.items || []).find(
        (row) => Number(row.id) === selectedItemId
      );

      if (!item) continue;

      selectedOptionItems.push({
        group_id: Number(group.id),
        item_id: Number(item.id),
        group_name: group.group_name || "",
        item_name: item.item_name || "",
        extra_price: Number(item.extra_price || 0),
      });
    }

    submitAddToCart(product, selectedOptionItems);
  }, [optionModal, submitAddToCart]);

  const selectedOptionTotal = useMemo(() => {
    const product = optionModal.product;
    if (!product) return 0;

    const groups = Array.isArray(product.option_groups)
      ? product.option_groups
      : [];

    return groups.reduce((sum, group) => {
      const itemId = Number(
        optionModal.selected[String(group.id)] || 0
      );

      const item = (group.items || []).find(
        (row) => Number(row.id) === itemId
      );

      return sum + Number(item?.extra_price || 0);
    }, 0);
  }, [optionModal]);

  const goToCart = useCallback(() => {
    const params = new URLSearchParams();
    if (queryShopId) params.set("shop_id", queryShopId);
    if (categoryId > 0) params.set("back_id", String(categoryId));
    navigate(`/kiosk/cart${params.toString() ? `?${params.toString()}` : ""}`);
  }, [categoryId, navigate, queryShopId]);

  const moveCategory = useCallback(
    (catId) => {
      const params = new URLSearchParams();
      if (queryShopId || shopId) params.set("shop_id", queryShopId || shopId);
      params.set("id", String(catId));
      if (mode) params.set("mode", mode);

      navigate(`/kiosk?${params.toString()}`);
      setMenuOpen(false);
    },
    [mode, navigate, queryShopId, shopId]
  );

  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]);

  useEffect(() => {
    refreshCartCount();
    pollKioskStatus();

    pollingRef.current.cart = setInterval(refreshCartCount, 1500);
    pollingRef.current.status = setInterval(pollKioskStatus, 1500);

    return () => {
      if (pollingRef.current.cart) clearInterval(pollingRef.current.cart);
      if (pollingRef.current.status) clearInterval(pollingRef.current.status);
      if (pollingRef.current.toastTimer) clearTimeout(pollingRef.current.toastTimer);
      if (pollingRef.current.paidTimer) clearTimeout(pollingRef.current.paidTimer);
    };
  }, [pollKioskStatus, refreshCartCount]);

  useEffect(() => {
    if (added === 1) {
      const name = addedName
        ? `장바구니에 담겼습니다: ${addedName}`
        : "장바구니에 담겼습니다.";

      setToastMessage(name);
      setShowToast(true);

      if (pollingRef.current.toastTimer) {
        clearTimeout(pollingRef.current.toastTimer);
      }

      pollingRef.current.toastTimer = setTimeout(() => {
        setShowToast(false);
      }, 400);

      const params = new URLSearchParams(location.search);
      params.delete("added");
      params.delete("added_name");

      navigate(
        `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
        { replace: true }
      );
    }
  }, [added, addedName, location.pathname, location.search, navigate]);

  useEffect(() => {
    const isKioskLock = new URLSearchParams(location.search).get("mode") === "lock";
    if (!isKioskLock) return;

    window.history.pushState({ kiosk: 1 }, "", window.location.href);

    const onPopState = () => {
      window.history.pushState({ kiosk: 1 }, "", window.location.href);
    };

    const onTouchStart = (ev) => {
      if (ev.touches && ev.touches[0]?.clientX < 20) {
        ev.preventDefault?.();
      }
    };

    window.addEventListener("popstate", onPopState);
    document.addEventListener("touchstart", onTouchStart, { passive: false });

    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("touchstart", onTouchStart);
    };
  }, [location.search]);

  const renderCategoryMenuSimple = (parentId = null) => {
    const children = getChildren(parentId);
    if (!children.length) return null;

    return children.map((cat) => {
      const childNodes = getChildren(cat.id);
      const hasChildren = childNodes.length > 0;

      if (hasChildren) {
        return (
          <div className="dropdown d-inline-block me-3 pc-dropdown" key={cat.id}>
            <button
              type="button"
              className="nav-link nav-link-btn"
              onClick={() => moveCategory(cat.id)}
            >
              {cat.name}
            </button>

            <ul className="dropdown-menu">
              {childNodes.map((subcat) => (
                <li key={subcat.id}>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => moveCategory(subcat.id)}
                  >
                    {subcat.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      return (
        <button
          type="button"
          key={cat.id}
          className="nav-link nav-link-btn me-2"
          onClick={() => moveCategory(cat.id)}
        >
          {cat.name}
        </button>
      );
    });
  };

  const renderCategoryMenuMobile = (parentId = null, level = 0) => {
    const children = getChildren(parentId);
    if (!children.length) return null;

    return (
      <ul
        className="list-unstyled"
        style={{ paddingLeft: `${level * 16}px`, marginBottom: 0 }}
      >
        {children.map((cat) => (
          <li className="mb-1" key={cat.id}>
            <button
              type="button"
              className="mobile-menu-link"
              onClick={() => moveCategory(cat.id)}
            >
              {cat.name}
            </button>
            {renderCategoryMenuMobile(cat.id, level + 1)}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <style>{`
        body {
          background-color: #fff;
          margin-bottom: 90px;
          overflow-x: hidden;
          font-family: sans-serif;
        }

        .kiosk-page {
          background: #fff;
          min-height: 100vh;
        }

        .top-bar {
          background-color: #fff;
          padding: 1rem;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          font-weight: bold;
          font-size: 1.1rem;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1100;
          overflow: visible !important;
        }

        .top-bar .logo {
          font-weight: 800;
          user-select: none;
          white-space: nowrap;
          color: inherit;
        }

        .center-wrap, .pc-menu {
          overflow: visible !important;
        }

        .top-bar .pc-menu {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          gap: 0.8rem;
          align-items: center;
          flex: none !important;
          max-width: 55vw;
          overflow-x: auto;
          white-space: nowrap;
          padding: 0 6px;
          font-weight: 600;
          font-size: 0.95rem;
          user-select: none;
        }

        .top-bar .icons {
          display: flex;
          gap: 1rem;
          font-size: 1.4rem;
          cursor: pointer;
          align-items: center;
          white-space: nowrap;
        }

        .top-bar .icons i {
          color: #333;
        }

        .nav-link-btn {
          padding: 0;
          border: 0;
          background: transparent;
          color: #222;
          text-decoration: none;
        }

        .nav-link-btn:hover {
          color: #0d6efd;
          text-decoration: underline;
        }

        #menuIcon {
          display: none;
        }

        .side-menu {
          position: fixed;
          top: 0;
          left: -260px;
          width: 260px;
          height: 100vh;
          background: #fff;
          box-shadow: 2px 0 8px rgba(0,0,0,0.15);
          z-index: 2200;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          transition: left 0.3s ease;
          font-weight: 500;
          overflow: hidden;
          -webkit-overflow-scrolling: touch;
        }

        .side-menu.show {
          left: 0;
        }

        .side-menu .menu-scroll {
          flex: 1 1 auto;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 24px;
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .side-menu .menu-scroll::-webkit-scrollbar {
          display: none;
        }

        .side-menu .close-btn {
          align-self: flex-end;
          font-size: 1.5rem;
          cursor: pointer;
          margin-bottom: 1rem;
          color: #666;
          background: transparent;
          border: 0;
        }

        .mobile-menu-link {
          display: block;
          width: 100%;
          text-align: left;
          border: 0;
          background: transparent;
          color: #222;
          text-decoration: none;
          padding: 6px 8px;
          border-radius: 6px;
        }

        .mobile-menu-link:hover {
          color: #0d6efd;
          background: #f8f9fa;
        }

        @media (max-width: 767.98px) {
          .top-bar .pc-menu {
            display: none !important;
          }

          #menuIcon {
            display: inline-block !important;
          }
        }

        @media (min-width: 768px) {
          .side-menu {
            display: none !important;
          }
        }

        .products-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          padding: 75px 15px;
        }

        .path-bar {
          font-size: 0.95rem;
          font-weight: 700;
          margin: 10px 0 18px;
          user-select: none;
        }

        .card-custom {
          border-radius: 0;
          box-shadow: none !important;
          margin-bottom: 15px;
          border: 0;
        }

        .card-custom img {
          height: auto;
          max-height: 220px;
          object-fit: cover;
        }

        .product-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 10px 6px;
        }

        .product-name {
          font-weight: 800;
          font-size: 0.95rem;
          margin: 0;
          line-height: 1.2;
          color: #222;
        }

        .price {
          font-weight: 800;
          font-size: 0.95rem;
          margin: 0;
          line-height: 1.2;
          color: #666;
        }

        .kiosk-card-btn {
          width: 100%;
          padding: 0;
          border: 0;
          background: transparent;
          text-align: left;
        }

        .kiosk-card-btn:active {
          transform: scale(0.99);
        }

        .product-image-wrap {
          position: relative;
        }

        .add-overlay {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.45);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 800;
          pointer-events: none;
          user-select: none;
        }

        .toast-overlay {
          display: block;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.35);
          z-index: 2400;
        }

        .toast-container-custom {
          position: fixed;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 2500;
        }

        .added-toast {
          width: min(92vw, 520px);
          border-radius: 18px;
          padding: 6px;
          box-shadow: 0 12px 40px rgba(0,0,0,.22);
          background: #212529;
          color: #fff;
        }

        .added-toast .toast-body {
          font-size: 20px;
          font-weight: 900;
          padding: 18px 16px;
          text-align: center;
        }

        .checkout-bar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 10px;
          background: #fff;
          border-top: 1px solid #ddd;
          z-index: 1500;
        }

        .paid-overlay {
          display: block;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.55);
          z-index: 3000;
        }

        .paid-overlay-card {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: min(92vw, 420px);
          background: #fff;
          border-radius: 16px;
          padding: 22px;
          text-align: center;
        }

        .cart-badge {
          padding: 0;
          min-width: 18px;
          height: 18px;
          font-size: 14px;
          border-radius: 50%;
          line-height: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }


        .kiosk-normal-price {
          color: #999;
          font-size: 0.82rem;
          text-decoration: line-through;
          line-height: 1.2;
        }

        .kiosk-sale-price {
          color: #dc3545;
          font-weight: 900;
          font-size: 0.95rem;
          line-height: 1.2;
        }

        .kiosk-option-label {
          color: #0d6efd;
          font-size: 0.82rem;
          font-weight: 700;
          margin-top: 2px;
        }

        .kiosk-option-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.5);
          z-index: 3200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
        }

        .kiosk-option-modal {
          width: min(92vw, 520px);
          max-height: 84vh;
          overflow-y: auto;
          background: #fff;
          border-radius: 18px;
          padding: 20px;
          box-shadow: 0 20px 60px rgba(0,0,0,.25);
        }

        .kiosk-option-group {
          border: 1px solid #e9ecef;
          border-radius: 12px;
          padding: 12px;
          margin-top: 12px;
        }

        .kiosk-option-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px;
          margin-top: 8px;
          border: 1px solid #e9ecef;
          border-radius: 10px;
          background: #fff;
          text-align: left;
        }

        .kiosk-option-item.selected {
          border-color: #212529;
          background: #f8f9fa;
          font-weight: 700;
        }

        @media (min-width: 768px) {
          .pc-dropdown:hover .dropdown-menu {
            display: block;
            margin-top: 0;
          }
        }
      `}</style>

      <div className="kiosk-page">
        <div className="top-bar">
          <div className="logo">{shopName}</div>

          <div className="center-wrap">
            <div className="pc-menu">{renderCategoryMenuSimple()}</div>
          </div>

          <div className="icons">
            <button
              type="button"
              onClick={goToCart}
              className="position-relative text-dark text-decoration-none border-0 bg-transparent p-0"
            >
              <i className="bi bi-bag fs-4"></i>
              {cartCount > 0 ? (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger cart-badge"
                  style={{ right: "-6px", top: "6px" }}
                >
                  {cartCount}
                </span>
              ) : null}
            </button>

            <i
              className="bi bi-list"
              title="메뉴"
              id="menuIcon"
              onClick={() => setMenuOpen(true)}
            ></i>
          </div>
        </div>

        {showToast ? <div className="toast-overlay" onClick={() => setShowToast(false)} /> : null}

        {showToast ? (
          <div className="toast-container-custom">
            <div className="added-toast">
              <div className="d-flex">
                <div className="toast-body">{toastMessage}</div>
                <button
                  type="button"
                  className="btn-close btn-close-white me-2 m-auto"
                  onClick={() => setShowToast(false)}
                ></button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="container products-wrapper">
          <div className="path-bar">
            <span className="fw-bold">{category?.name || ""}</span>
          </div>

          {loading ? (
            <div className="py-5 text-center">로딩 중...</div>
          ) : (
            <div className="row row-cols-2 row-cols-md-4 g-3 g-md-4">
              {products.map((product) => (
                <div className="col" key={product.id}>
                  <button
                    type="button"
                    className="kiosk-card-btn"
                    onClick={() => handleAddToCart(product)}
                  >
                    <div className="card card-custom h-100">
                      {product.image_path ? (
                        <div className="product-image-wrap">
                          <img
                            src={
                              product.image_path.startsWith("http")
                                ? product.image_path
                                : `/static/${String(product.image_path).replace(/^\/+/, "")}`
                            }
                            className="card-img-top"
                            alt="상품 이미지"
                          />
                          <div className="add-overlay">+</div>
                        </div>
                      ) : null}

                      <div className="product-info">
                        <div className="product-name">{product.name}</div>

                        {(() => {
                          const originalPrice = Number(
                            product.original_price ??
                            product.normal_price ??
                            product.price ??
                            0
                          );

                          const displayPrice = Number(
                            product.display_price ??
                            product.sale_price ??
                            product.price ??
                            0
                          );

                          const isSale =
                            Boolean(product.is_sale) ||
                            (
                              originalPrice > 0 &&
                              displayPrice > 0 &&
                              displayPrice < originalPrice
                            );

                          const hasOptions =
                            Array.isArray(product.option_groups) &&
                            product.option_groups.length > 0;

                          return (
                            <>
                              {isSale ? (
                                <>
                                  <div className="kiosk-normal-price">
                                    {originalPrice.toLocaleString()}원
                                  </div>
                                  <div className="kiosk-sale-price">
                                    {displayPrice.toLocaleString()}원
                                  </div>
                                </>
                              ) : (
                                <div className="price">
                                  {displayPrice.toLocaleString()}원
                                </div>
                              )}

                              {hasOptions ? (
                                <div className="kiosk-option-label">
                                  옵션 선택
                                </div>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>


        {optionModal.open && optionModal.product ? (
          <div
            className="kiosk-option-overlay"
            onClick={() =>
              setOptionModal({
                open: false,
                product: null,
                selected: {},
              })
            }
          >
            <div
              className="kiosk-option-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div>
                  <div className="fw-bold fs-5">
                    {optionModal.product.name}
                  </div>
                  <div className="text-muted small mt-1">
                    상품 옵션을 선택해주세요.
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setOptionModal({
                      open: false,
                      product: null,
                      selected: {},
                    })
                  }
                />
              </div>

              {(optionModal.product.option_groups || []).map((group) => {
                const selectedId = Number(
                  optionModal.selected[String(group.id)] || 0
                );

                return (
                  <div className="kiosk-option-group" key={group.id}>
                    <div className="fw-bold">
                      {group.group_name}
                      {String(group.required || "N").toUpperCase() === "Y" ? (
                        <span className="text-danger ms-1">*</span>
                      ) : null}
                    </div>

                    {(group.items || []).map((item) => {
                      const selected =
                        selectedId === Number(item.id);

                      return (
                        <button
                          key={item.id}
                          type="button"
                          className={`kiosk-option-item ${
                            selected ? "selected" : ""
                          }`}
                          onClick={() =>
                            handleOptionChange(group.id, item.id)
                          }
                        >
                          <span>
                            {selected ? "●" : "○"}
                          </span>

                          <span className="flex-grow-1">
                            {item.item_name}
                          </span>

                          {Number(item.extra_price || 0) !== 0 ? (
                            <span>
                              {Number(item.extra_price || 0) > 0 ? "+" : ""}
                              {Number(item.extra_price || 0).toLocaleString()}원
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                );
              })}

              <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                <span className="fw-bold">총 금액</span>
                <span className="fw-bold fs-5">
                  {(
                    Number(
                      optionModal.product.display_price ??
                      optionModal.product.sale_price ??
                      optionModal.product.price ??
                      0
                    ) + selectedOptionTotal
                  ).toLocaleString()}원
                </span>
              </div>

              <button
                type="button"
                className="btn btn-dark w-100 py-3"
                onClick={confirmOptionProduct}
              >
                장바구니 담기
              </button>
            </div>
          </div>
        ) : null}

        <nav className={`side-menu ${menuOpen ? "show" : ""}`} role="navigation" aria-label="모바일 메뉴">
          <button
            type="button"
            className="close-btn"
            onClick={() => setMenuOpen(false)}
            aria-label="메뉴 닫기"
          >
            &times;
          </button>

          <div className="mb-2 fw-bold">{shopName}</div>

          <div className="menu-scroll">{renderCategoryMenuMobile()}</div>
        </nav>

        {showCheckoutBar ? (
          <div className="checkout-bar">
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary w-25"
                onClick={() => {
                  if (window.confirm("처음부터 다시 시작할까요?")) {
                    handleReset();
                  }
                }}
              >
                처음부터
              </button>

              <button onClick={goToCart} className="btn btn-dark w-75 py-3">
                결제하기
                <span className="ms-2 badge bg-light text-dark">{cartCount}개</span>
              </button>
            </div>
          </div>
        ) : null}

        {showPaidOverlay ? (
          <div className="paid-overlay">
            <div className="paid-overlay-card">
              <div style={{ fontSize: "20px", fontWeight: 900, marginBottom: "8px" }}>
                ✅ 결제 완료
              </div>
              <div style={{ color: "#666", fontSize: "14px", marginBottom: "14px" }}>
                손님 결제가 완료되었습니다.
              </div>
              <button className="btn btn-dark w-100" onClick={() => handleReset()}>
                새 주문 받기
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default KioskHome;